import type { CustomFieldsParams, CustomFieldsQueryInput } from '../-types';

/**
 * The columns the list maps into its `ORDER BY` —
 * `AdminC2cDefaultQuestionsListSortColumns` in `modules/c2c-custom-questions`.
 * The querystring schema rejects anything else with a 400, so an unlisted sort
 * is dropped rather than sent.
 */
const SORTABLE_FIELDS = new Set([
  'title',
  'type',
  'apiFieldIdentifier',
  'updatedAt',
  'createdAt',
]);

/**
 * The columns the list filters on. `title` and
 * `apiFieldIdentifier` are `LIKE` matches, `type` is an exact match, which is
 * why the type filter is single-choice: a comma-joined pair would be compared
 * as one literal and match nothing.
 */
const FILTER_FIELDS = ['title', 'type', 'apiFieldIdentifier'] as const;

/** Reads a single-valued filter off the table's multi-value filter map. */
function first(
  filters: Record<string, string[]> | undefined,
  key: string,
): string | undefined {
  const value = filters?.[key]?.[0];
  return value && value.length > 0 ? value : undefined;
}

/** The JSON `filter` blob, or nothing when no column is narrowed. */
export function buildCustomFieldsFilterJson(
  filters: Record<string, string[]> | undefined,
): string | undefined {
  const out: Record<string, string> = {};
  for (const key of FILTER_FIELDS) {
    const value = first(filters, key);
    if (value) out[key] = value;
  }
  return Object.keys(out).length > 0 ? JSON.stringify(out) : undefined;
}

/** Turns table state into the endpoint's parameters. */
export function toCustomFieldsParams(
  input: CustomFieldsQueryInput,
): CustomFieldsParams {
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;
  const filter = buildCustomFieldsFilterJson(input.filters);

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(filter ? { filter } : {}),
  };
}

export function buildCustomFieldsQs(params: CustomFieldsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
